import { z } from "zod";
import { FieldConfig, FieldType } from "./types";
import {
  validateCPF,
  validateCNPJ,
  validateCEP,
} from "@/lib/custom-validations";

/**
 * Constrói um schema Zod a partir de uma lista de configurações de campos
 */
export function buildSchema(
  fields: FieldConfig[]
): z.ZodObject<Record<string, z.ZodTypeAny>> {
  const shape: Record<string, z.ZodTypeAny> = {};

  for (const field of fields) {
    shape[field.name] = buildFieldSchema(field);
  }

  return z.object(shape);
}

/**
 * Constrói o schema Zod para um campo individual
 */
function buildFieldSchema(field: FieldConfig): z.ZodTypeAny {
  let schema: z.ZodTypeAny;
  const label = field.label || field.name;

  switch (field.type) {
    case "text":
    case "textarea":
      schema = buildTextSchema(field, label);
      break;

    case "email":
      schema = buildEmailSchema(field, label);
      break;

    case "password":
      schema = buildPasswordSchema(field, label);
      break;

    case "cpf":
      schema = buildCpfSchema(field, label);
      break;

    case "cnpj":
      schema = buildCnpjSchema(field, label);
      break;

    case "cpf-cnpj":
      schema = buildCpfCnpjSchema(field, label);
      break;

    case "phone":
      schema = buildPhoneSchema(field, label);
      break;

    case "cep":
      schema = buildCepSchema(field, label);
      break;

    case "rg":
      schema = buildRgSchema(field, label);
      break;

    case "number":
      schema = buildNumberSchema(field, label);
      break;

    case "currency":
      schema = buildCurrencySchema(field, label);
      break;

    case "date":
      schema = buildDateSchema(field, label);
      break;

    case "select":
      schema = buildSelectSchema(field, label);
      break;

    case "multi-select":
      schema = buildMultiSelectSchema(field, label);
      break;

    case "checkbox":
      schema = buildCheckboxSchema(field, label);
      break;

    case "radio":
      schema = buildRadioSchema(field, label);
      break;

    case "file":
    case "file-preview":
      schema = buildFileSchema(field, label);
      break;

    case "custom":
    default:
      schema = z.any();
  }

  // Aplicar validação customizada
  if (field.validate) {
    const validateFn = field.validate;
    schema = schema.superRefine((val, ctx) => {
      const result = validateFn(val, {});
      if (result !== true) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: result,
        });
      }
    });
  }

  // Campo opcional (se não for required)
  if (!field.required) {
    schema = schema.optional();
  }

  return schema;
}

// ============================================================
// Schema Builders por Tipo
// ============================================================

function buildTextSchema(field: FieldConfig, label: string): z.ZodString {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, `${label} é obrigatório`);
  }

  if (field.minLength) {
    schema = schema.min(
      field.minLength,
      `${label} deve ter no mínimo ${field.minLength} caracteres`
    );
  }

  if (field.maxLength) {
    schema = schema.max(
      field.maxLength,
      `${label} deve ter no máximo ${field.maxLength} caracteres`
    );
  }

  if (field.pattern) {
    schema = schema.regex(field.pattern, `${label} possui formato inválido`);
  }

  return schema;
}

function buildEmailSchema(field: FieldConfig, label: string): z.ZodString {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, `${label} é obrigatório`);
  }

  schema = schema.email("E-mail inválido");

  return schema;
}

function buildPasswordSchema(field: FieldConfig, label: string): z.ZodString {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, `${label} é obrigatório`);
  }

  if (field.minLength) {
    schema = schema.min(
      field.minLength,
      `${label} deve ter no mínimo ${field.minLength} caracteres`
    );
  }

  return schema;
}

function buildCpfSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.string();

  if (field.required) {
    schema = (schema as z.ZodString).min(1, `${label} é obrigatório`);
  }

  schema = schema.refine(
    (val) => {
      if (!val) return !field.required;
      return validateCPF(String(val).replace(/\D/g, ""));
    },
    { message: "CPF inválido" }
  );

  return schema;
}

function buildCnpjSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.string();

  if (field.required) {
    schema = (schema as z.ZodString).min(1, `${label} é obrigatório`);
  }

  schema = schema.refine(
    (val) => {
      if (!val) return !field.required;
      return validateCNPJ(String(val).replace(/\D/g, ""));
    },
    { message: "CNPJ inválido" }
  );

  return schema;
}

function buildCpfCnpjSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.string();

  if (field.required) {
    schema = (schema as z.ZodString).min(1, `${label} é obrigatório`);
  }

  schema = schema.refine(
    (val) => {
      if (!val) return !field.required;
      const digits = String(val).replace(/\D/g, "");
      if (digits.length === 11) {
        return validateCPF(digits);
      }
      if (digits.length === 14) {
        return validateCNPJ(digits);
      }
      return false;
    },
    { message: "CPF ou CNPJ inválido" }
  );

  return schema;
}

function buildPhoneSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.string();

  if (field.required) {
    schema = (schema as z.ZodString).min(1, `${label} é obrigatório`);
  }

  schema = schema.refine(
    (val) => {
      if (!val) return !field.required;
      return String(val).replace(/\D/g, "").length >= 10;
    },
    { message: "Telefone inválido" }
  );

  return schema;
}

function buildCepSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.string();

  if (field.required) {
    schema = (schema as z.ZodString).min(1, `${label} é obrigatório`);
  }

  schema = schema.refine(
    (val) => {
      if (!val) return !field.required;
      return validateCEP(String(val));
    },
    { message: "CEP inválido" }
  );

  return schema;
}

function buildRgSchema(field: FieldConfig, label: string): z.ZodString {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, `${label} é obrigatório`);
  }

  if (field.minLength) {
    schema = schema.min(
      field.minLength,
      `${label} deve ter no mínimo ${field.minLength} caracteres`
    );
  }

  return schema;
}

function buildNumberSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema = z.coerce.number();

  if (field.required) {
    schema = schema.min(0.01, `${label} é obrigatório`);
  }

  if (field.min !== undefined) {
    schema = schema.min(field.min, `${label} deve ser no mínimo ${field.min}`);
  }

  if (field.max !== undefined) {
    schema = schema.max(field.max, `${label} deve ser no máximo ${field.max}`);
  }

  return schema;
}

function buildCurrencySchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(0.01, `${label} é obrigatório`);
  }

  if (field.min !== undefined) {
    schema = schema.min(field.min, `Valor mínimo: R$ ${field.min}`);
  }

  if (field.max !== undefined) {
    schema = schema.max(field.max, `Valor máximo: R$ ${field.max}`);
  }

  return schema;
}

function buildDateSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.coerce.date();

  if (field.required) {
    schema = schema.refine((val: Date | null) => val !== null, {
      message: `${label} é obrigatório`,
    });
  }

  return schema;
}

function buildSelectSchema(field: FieldConfig, label: string): z.ZodString {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, `${label} é obrigatório`);
  }

  return schema;
}

function buildMultiSelectSchema(
  field: FieldConfig,
  label: string
): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.array(z.string());

  if (field.required) {
    schema = (schema as z.ZodArray<z.ZodString>).min(
      1,
      `Selecione ao menos uma opção de ${label}`
    );
  }

  return schema;
}

function buildCheckboxSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z.boolean();

  if (field.required) {
    schema = schema.refine((val: boolean) => val === true, {
      message: `${label} é obrigatório`,
    });
  }

  return schema;
}

function buildRadioSchema(field: FieldConfig, label: string): z.ZodString {
  let schema = z.string();

  if (field.required) {
    schema = schema.min(1, `${label} é obrigatório`);
  }

  return schema;
}

function buildFileSchema(field: FieldConfig, label: string): z.ZodTypeAny {
  let schema: z.ZodTypeAny = z
    .instanceof(File)
    .nullable()
    .or(z.undefined());

  if (field.required) {
    schema = z.instanceof(File, { message: `${label} é obrigatório` });
  }

  return schema;
}

/**
 * Extrai os valores default dos campos
 */
export function extractDefaultValues(
  fields: FieldConfig[]
): Record<string, unknown> {
  return fields.reduce(
    (acc, field) => {
      if (field.defaultValue !== undefined) {
        acc[field.name] = field.defaultValue;
      } else {
        // Define valores padrão por tipo
        acc[field.name] = getDefaultValueByType(field.type);
      }
      return acc;
    },
    {} as Record<string, unknown>
  );
}

/**
 * Retorna o valor padrão baseado no tipo do campo
 */
function getDefaultValueByType(type: FieldType): unknown {
  switch (type) {
    case "checkbox":
      return false;
    case "multi-select":
      return [];
    case "number":
    case "currency":
      return undefined;
    case "file":
      return null;
    default:
      return "";
  }
}
